import React, { useState } from 'react';
import { Check } from 'lucide-react';
import biomarkersScreen from '@/assets/images/landing/feature-screens/biomarkers.svg';
import clientManagementScreen from '@/assets/images/landing/feature-screens/client-management.svg';
import invoiceBillingScreen from '@/assets/images/landing/feature-screens/invoice-billing.svg';
import mealPlanScreen from '@/assets/images/landing/feature-screens/meal-plan.svg';
import mealPlanAnalysisScreen from '@/assets/images/landing/feature-screens/meal-plan-analysis.svg';
import questionnairesScreen from '@/assets/images/landing/feature-screens/questionnaires.svg';
import scaleCard1Video from '@/assets/videos/landing/scale/scale-card-1.mp4';
import scaleCard2Video from '@/assets/videos/landing/scale/scale-card-2.mp4';
import scaleCard3Video from '@/assets/videos/landing/scale/scale-card-3.mp4';
import scaleCard4Video from '@/assets/videos/landing/scale/scale-card-4.mp4';
import styles from './Features.module.css';

const workflowScreens = [
  {
    label: 'Client Management',
    title: 'Keep every client moving without losing the thread',
    description:
      'A focused client workspace brings progress, goals, plan status, and next actions into one calm view.',
    image: clientManagementScreen,
    alt: 'Client management dashboard preview'
  },
  {
    label: 'Questionnaires',
    title: 'Collect the details you need before the session starts',
    description:
      'Structured intake turns scattered back-and-forth into clear nutrition, lifestyle, and medical context.',
    image: questionnairesScreen,
    alt: 'Questionnaire workflow preview'
  },
  {
    label: 'Biomarkers',
    title: 'Turn lab reports into real nutrition insights',
    description:
      'Track biomarker ranges, trends, and clinical context so recommendations stay grounded in client data.',
    image: biomarkersScreen,
    alt: 'Biomarkers dashboard preview'
  },
  {
    label: 'Meal Analysis',
    title: 'See nutritional balance before the plan leaves your desk',
    description:
      'Analysis views make calories, macros, and meal-level patterns easier to review and refine.',
    image: mealPlanAnalysisScreen,
    alt: 'Meal analysis dashboard preview'
  },
  {
    label: 'Intelligent Meal Planning',
    title: 'Build practical plans from real client context',
    description:
      'Plan meals, review portions, and adjust recommendations without rebuilding the same workflow each week.',
    image: mealPlanScreen,
    alt: 'Intelligent meal planning workspace preview'
  },
  {
    label: 'Scheduling, Billing and Invoices',
    title: 'Close the loop with invoices built into practice workflows',
    description:
      'Billing stays connected to the same client journey, so admin does not become its own second job.',
    image: invoiceBillingScreen,
    alt: 'Invoice and billing dashboard preview'
  }
];

const scaleCards = [
  {
    label: 'Structured intake',
    title: 'Start with structured client information',
    description:
      'Structured questionnaires capture what you actually need, upfront. No more chasing clients for missing medical history or dietary preferences.',
    points: [
      'Use guided questionnaires to collect consistent information',
      'Capture lifestyle, medical history, and goals in one flow'
    ],
    image: questionnairesScreen,
    video: scaleCard1Video
  },
  {
    label: 'Faster planning',
    title: 'Less admin. More actual practice',
    description:
      'Automate repetitive tasks like intake, planning, and follow-ups so your time goes into care, not coordination.',
    points: [
      'Generate meal plans without starting from scratch each time',
      'Reuse and adapt existing structures easily'
    ],
    image: mealPlanScreen,
    video: scaleCard2Video
  },
  {
    label: 'Client clarity',
    title: 'Handle more clients without losing context',
    description:
      'Keep every client organized and accessible. One command center gives you a 360-degree view of your entire practice at a glance.',
    points: ['View complete client information in one place', 'Smart filtering and tagging'],
    image: clientManagementScreen,
    video: scaleCard3Video
  },
  {
    label: 'Practice operations',
    title: 'Run your practice without extra effort',
    description:
      'Manage scheduling, billing, and coordination without adding to your daily workload.',
    points: [
      'Create and send invoices directly from client workflows',
      'Manage sessions and availability in one place'
    ],
    image: invoiceBillingScreen,
    video: scaleCard4Video
  }
];

const playPreviewVideo = (event: React.SyntheticEvent<HTMLVideoElement>) => {
  const video = event.currentTarget;

  if (!video.paused) {
    return;
  }

  void video.play().catch(() => undefined);
};

const Features = ({
  showWorkflowTabs = true,
  showOutcomeText = true,
}: {
  showWorkflowTabs?: boolean;
  showOutcomeText?: boolean;
}) => {
  const [activeWorkflowIndex, setActiveWorkflowIndex] = useState(0);
  const activeWorkflow = workflowScreens[activeWorkflowIndex];

  return (
    <section id="features" className={styles.featuresSection}>
      <div className={styles.featuresCanvas}>
        <h2 className={styles.title}>
          How CalorieScience helps you scale <span>3X</span>
        </h2>

        <div className={styles.cardShowcase} aria-label="Scale workflow highlights">
          <div className={styles.cardBackplate} aria-hidden="true" />
          <div className={styles.cardStack}>
            {scaleCards.map((card, index) => (
              <article
                key={card.title}
                className={styles.scaleCard}
                style={{ '--card-index': index } as React.CSSProperties}
              >
                <div className={`${styles.documentPanel} ${card.video ? styles.videoPanel : ''}`}>
                  {card.video ? (
                    <video
                      src={card.video}
                      className={`${styles.cardVisual} ${styles.cardVideo}`}
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      poster={card.image}
                      aria-hidden="true"
                      onCanPlay={playPreviewVideo}
                    />
                  ) : (
                    <img src={card.image} alt="" className={styles.cardVisual} loading="lazy" />
                  )}
                </div>

                <div className={styles.cardCopy}>
                  <p className={styles.cardLabel}>{card.label}</p>
                  <h3>{card.title}</h3>
                  <p className={styles.cardDescription}>{card.description}</p>
                  <ul className={styles.pointList}>
                    {card.points.map(point => (
                      <li key={point}>
                        <Check aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                  <span className={styles.stepNumber}>{String(index + 1).padStart(2, '0')}</span>
                </div>
              </article>
            ))}
          </div>
        </div>

        {showOutcomeText && (
          <p className={styles.outcomeText}>
            More clients. The same care.
            <br />
            More time for the work you love.
          </p>
        )}

        {showWorkflowTabs && (
          <>
            <div className={styles.workflowTabs} aria-label="Feature categories">
              {workflowScreens.map((screen, index) => (
                <button
                  type="button"
                  key={screen.label}
                  className={index === activeWorkflowIndex ? styles.activeTab : undefined}
                  aria-pressed={index === activeWorkflowIndex}
                  onClick={() => setActiveWorkflowIndex(index)}
                >
                  {screen.label}
                </button>
              ))}
            </div>

            <div className={styles.productPreview}>
              <div className={styles.previewCopy}>
                <p className={styles.previewEyebrow}>{activeWorkflow.label}</p>
                <h3>{activeWorkflow.title}</h3>
                <p>{activeWorkflow.description}</p>
              </div>
              <div className={styles.previewFrame}>
                <img src={activeWorkflow.image} alt={activeWorkflow.alt} className={styles.previewImage} />
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Features;
